// src/modals/PricingConfigEditModal.jsx — Phase 6 PR3 (ADR-094)
//
// Modal édition d'une row `logement_pricing_config`.
//
// Fonctions :
//   - Form 5 champs avec validation client-side miroir validation server-side
//   - LPR3-3 auto-fill `pricelabs_url` UNIQUEMENT si vide (NO overwrite si saisie manuelle)
//   - MPR3-4 If-Match optimistic lock : stocke `loaded_updated_at` au chargement, l'envoie au PATCH
//   - LPR3-2 Esc keyboard + click overlay close (calque ResaV2Modal.jsx:70-81)
//   - Soumission POST (nouveau) ou PATCH (existant) via prop `onSubmit`
//   - PR3 fix UX commit 10 : beds24_room_id / beds24_property_id auto-remplis
//     depuis logements_beds24_mapping (lecture seule, source de vérité unique).
//     Bloque submit si mode=manual et mapping absent.

import { useEffect, useMemo, useState } from "react";
import { Settings, Plus, AlertTriangle } from "lucide-react";
import { DS } from "../lib/ui";
import { getBeds24Mapping } from "../lib/beds24-mapping-lookup";

const LOGEMENT_ID_REGEX = /^[a-z0-9.]+$/i;
const DIGITS_ONLY_REGEX = /^\d+$/;

function validate(form, { mapping, mappingLoading } = {}) {
  const errors = {};
  if (!form.logement_id || !LOGEMENT_ID_REGEX.test(form.logement_id)) {
    errors.logement_id = "ID requis (regex /^[a-z0-9.]+$/i, ex: p1, p6.lc1)";
  }
  if (!["pricelabs", "manual"].includes(form.pricing_mode)) {
    errors.pricing_mode = "Mode requis";
  }
  if (form.pricing_mode === "pricelabs") {
    if (!form.pricelabs_listing_id) {
      errors.pricelabs_listing_id =
        "PriceLabs Listing ID requis si pricing_mode=pricelabs";
    } else if (!DIGITS_ONLY_REGEX.test(String(form.pricelabs_listing_id))) {
      errors.pricelabs_listing_id = "Digits uniquement";
    }
  }
  // PR3 fix UX commit 10 — Mode manual : mapping Beds24 OBLIGATOIRE
  // (résolu côté serveur depuis logements_beds24_mapping). Tant que le
  // mapping est en cours de chargement, on ne bloque pas (laisse le user
  // remplir le reste). Une fois chargé : si null + mode=manual → erreur.
  if (form.pricing_mode === "manual" && !mappingLoading && !mapping) {
    errors.beds24_mapping =
      "Mapping Beds24 manquant pour ce logement. Configurer d'abord dans Paramètres > Beds24.";
  }
  return errors;
}

export default function PricingConfigEditModal({
  existing,
  logementIdDefault,
  allLogIds,
  onClose,
  onSubmit,
}) {
  const isPatch = !!existing;

  // État form initial : si existing → patch, sinon création
  const [form, setForm] = useState(() => ({
    logement_id: existing?.logement_id || logementIdDefault || "",
    pricing_mode: existing?.pricing_mode || "manual",
    pricelabs_listing_id: existing?.pricelabs_listing_id || "",
    pricelabs_url: existing?.pricelabs_url || "",
    beds24_property_id: existing?.beds24_property_id || "",
    beds24_room_id: existing?.beds24_room_id || "",
    marketplace_active: existing?.marketplace_active === true,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  // PR3 fix UX commit 10 — Lookup mapping Beds24 read-only (source de vérité
  // unique = logements_beds24_mapping). Champs beds24_room_id/property_id
  // pré-remplis automatiquement, plus de double saisie côté admin.
  const [mapping, setMapping] = useState(null); // { beds24_property_id, beds24_room_id } | null
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const id = form.logement_id;
    if (!id || !LOGEMENT_ID_REGEX.test(id)) {
      setMapping(null);
      setMappingLoading(false);
      return undefined;
    }
    setMappingLoading(true);
    setMappingError(null);
    getBeds24Mapping(id)
      .then((row) => {
        if (cancelled) return;
        setMapping(row || null);
        setMappingLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setMappingError(e?.message || "lookup_failed");
        setMapping(null);
        setMappingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.logement_id]);

  // Sync form avec mapping résolu (pour affichage cohérent read-only)
  useEffect(() => {
    if (mapping) {
      setForm((f) => ({
        ...f,
        beds24_room_id: mapping.beds24_room_id || "",
        beds24_property_id: mapping.beds24_property_id || "",
      }));
    } else if (!mappingLoading) {
      // Mapping absent → vider pour cohérence visuelle
      setForm((f) => ({
        ...f,
        beds24_room_id: "",
        beds24_property_id: "",
      }));
    }
  }, [mapping, mappingLoading]);

  // MPR3-4 If-Match : snapshot du updated_at au chargement (immuable)
  const loadedUpdatedAt = useMemo(
    () => existing?.updated_at || null,
    [existing],
  );

  const errors = useMemo(
    () => validate(form, { mapping, mappingLoading }),
    [form, mapping, mappingLoading],
  );
  const isValid = Object.keys(errors).length === 0 && !mappingLoading;

  // LPR3-2 Esc keyboard handler (calque ResaV2Modal.jsx:70-81)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function setField(name, value) {
    setForm((f) => {
      const next = { ...f, [name]: value };
      // LPR3-3 auto-fill pricelabs_url UNIQUEMENT si vide (NO overwrite)
      if (
        name === "pricelabs_listing_id" &&
        value &&
        DIGITS_ONLY_REGEX.test(String(value)) &&
        !f.pricelabs_url
      ) {
        next.pricelabs_url = `https://app.pricelabs.co/pricing?listings=${value}`;
      }
      // Si on bascule en pricelabs, désactive marketplace par défaut (jamais auto-activé)
      if (name === "pricing_mode" && value === "manual") {
        next.marketplace_active = false;
      }
      return next;
    });
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setServerError(null);

    // Normalisation payload : digits-only fields → null si vide.
    // PR3 fix UX commit 10 — beds24_room_id et beds24_property_id ne sont
    // PAS envoyés depuis le client : le backend les résout depuis
    // logements_beds24_mapping (source de vérité unique, anti-bypass curl).
    const payload = {
      logement_id: form.logement_id,
      pricing_mode: form.pricing_mode,
      pricelabs_listing_id: form.pricelabs_listing_id || null,
      pricelabs_url: form.pricelabs_url || null,
      marketplace_active: form.marketplace_active === true,
    };
    if (isPatch) payload._existing = true;

    // MPR3-4 If-Match optimistic lock
    const opts = {};
    if (isPatch && loadedUpdatedAt) opts.ifMatch = loadedUpdatedAt;

    const result = await onSubmit(payload, opts);
    setSubmitting(false);
    if (!result?.ok && !result?.conflict) {
      setServerError(result?.error || "Erreur serveur");
    }
    // Si OK ou conflict → parent ferme la modal
  }

  // Affichage conditionnel champs PriceLabs vs Beds24
  const showPricelabs = form.pricing_mode === "pricelabs";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        className="fbz-modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: DS.card,
          borderRadius: 12,
          maxWidth: 540,
          width: "100%",
          maxHeight: "92vh",
          overflowY: "auto",
          padding: 22,
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontDisplay,
              fontSize: 18,
              fontWeight: 600,
              color: DS.marineDark,
            }}
          >
            {isPatch
              ? <><Settings size={18} strokeWidth={2.2} /> Éditer config pricing</>
              : <><Plus size={18} strokeWidth={2.4} /> Nouveau logement pricing</>}
          </div>
          <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 4 }}>
            {isPatch
              ? `Logement : ${existing.logement_id}`
              : "Choisir un logement V1 KV existant."}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* logement_id */}
          <div style={fieldRow}>
            <label style={labelStyle}>Logement ID *</label>
            {isPatch ? (
              <input
                value={form.logement_id}
                disabled
                style={{ ...inputStyle, background: DS.pageBg, color: DS.textSecondary }}
              />
            ) : (
              <input
                value={form.logement_id}
                onChange={(e) => setField("logement_id", e.target.value)}
                placeholder="ex: p1, p6.lc1"
                list="pricing-log-ids"
                style={inputStyle}
              />
            )}
            {!isPatch && allLogIds && allLogIds.length > 0 && (
              <datalist id="pricing-log-ids">
                {allLogIds.map((id) => (
                  <option key={id} value={id} />
                ))}
              </datalist>
            )}
            {errors.logement_id && (
              <div style={errorStyle}>{errors.logement_id}</div>
            )}
          </div>

          {/* pricing_mode — PR #167d (Q-4) : désactivé. Modes obsolète post-D-1.
              D-1 : backend crée auto config TYPE B `manual` au POST mapping.
              Bascule TYPE B ↔ TYPE A → bouton 🔀 dans tableau Mappings (Vue 2). */}
          <div style={fieldRow}>
            <label style={labelStyle}>Mode pricing</label>
            <input
              value={
                form.pricing_mode === "manual"
                  ? "manual (FBZ source de vérité)"
                  : "pricelabs (PriceLabs source de vérité)"
              }
              disabled
              style={{ ...inputStyle, background: DS.pageBg, color: DS.textSecondary }}
              data-testid="pricing-mode-readonly"
            />
            <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 3 }}>
              Pour changer de mode → bouton <strong>🔀 (Basculer)</strong> dans
              le tableau Mappings actifs. (Cf. PR #167d / ADR-102 Q-4.)
            </div>
          </div>

          {/* Champs PriceLabs (visibles seulement si mode=pricelabs) */}
          {showPricelabs && (
            <>
              <div style={fieldRow}>
                <label style={labelStyle}>PriceLabs Listing ID *</label>
                <input
                  value={form.pricelabs_listing_id}
                  onChange={(e) =>
                    setField("pricelabs_listing_id", e.target.value)
                  }
                  placeholder="ex: 85595087785"
                  style={inputStyle}
                />
                {errors.pricelabs_listing_id && (
                  <div style={errorStyle}>{errors.pricelabs_listing_id}</div>
                )}
              </div>
              <div style={fieldRow}>
                <label style={labelStyle}>PriceLabs URL</label>
                <input
                  value={form.pricelabs_url}
                  onChange={(e) => setField("pricelabs_url", e.target.value)}
                  placeholder="auto-générée si vide"
                  style={inputStyle}
                />
                <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 3 }}>
                  Auto-remplie si vide à la saisie du Listing ID (LPR3-3). Non
                  écrasée si saisie manuelle.
                </div>
              </div>
              <div style={fieldRow}>
                <label
                  style={{
                    ...labelStyle,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.marketplace_active === true}
                    onChange={(e) =>
                      setField("marketplace_active", e.target.checked)
                    }
                  />
                  Marketplace actif (push automatique PriceLabs → Beds24)
                </label>
              </div>
            </>
          )}

          {/* PR3 fix UX commit 10 — Bandeau mapping Beds24 (read-only).
              Source de vérité unique = logements_beds24_mapping. */}
          <div style={fieldRow}>
            <label style={labelStyle}>Mapping Beds24 (lecture seule)</label>
            {mappingLoading ? (
              <div style={mappingBoxNeutral}>Lecture du mapping…</div>
            ) : mappingError ? (
              <div style={{ ...mappingBoxError, display: "flex", gap: 7, alignItems: "flex-start" }}>
                <AlertTriangle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>Erreur lecture mapping : {mappingError}</span>
              </div>
            ) : mapping ? (
              <div style={mappingBoxOk}>
                <div style={{ fontSize: 12 }}>
                  <strong>Room ID :</strong>{" "}
                  <code style={codeStyle}>{mapping.beds24_room_id || "—"}</code>
                  <span style={{ margin: "0 8px", color: DS.textMuted }}>·</span>
                  <strong>Property ID :</strong>{" "}
                  <code style={codeStyle}>
                    {mapping.beds24_property_id || "—"}
                  </code>
                </div>
                <div style={{ fontSize: 10, color: DS.successText, marginTop: 4 }}>
                  Auto-rempli depuis logements_beds24_mapping. Modifier dans{" "}
                  <strong>Paramètres &gt; Beds24</strong>.
                </div>
              </div>
            ) : (
              <div style={mappingBoxWarn}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700 }}>
                  <AlertTriangle size={13} strokeWidth={2.3} /> Aucun mapping Beds24 actif
                </div>
                <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>
                  Configurer d'abord le mapping dans{" "}
                  <strong>Paramètres &gt; Beds24</strong> pour ce logement
                  avant de créer une config pricing en mode <em>manual</em>.
                </div>
              </div>
            )}
            {errors.beds24_mapping && (
              <div style={{ ...errorStyle, marginTop: 6 }}>
                {errors.beds24_mapping}
              </div>
            )}
          </div>

          {serverError && (
            <div
              style={{
                ...errorStyle,
                display: "flex",
                gap: 7,
                alignItems: "flex-start",
                padding: 8,
                background: DS.dangerBg,
                borderRadius: 6,
                marginTop: 8,
              }}
            >
              <AlertTriangle size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>{serverError}</span>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 18,
            }}
          >
            <button type="button" onClick={onClose} style={btnSecondary}>
              Annuler
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting}
              style={{
                ...btnPrimary,
                opacity: !isValid || submitting ? 0.5 : 1,
                cursor: !isValid || submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting
                ? isPatch
                  ? "⟳ Sauvegarde…"
                  : form.pricing_mode === "manual"
                    ? "⟳ Création + lecture prix Beds24…"
                    : "⟳ Sauvegarde…"
                : isPatch
                  ? "Mettre à jour"
                  : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fieldRow = { marginBottom: 12 };
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: DS.navy,
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: DS.radiusControl,
  border: `0.5px solid ${DS.borderHover}`,
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  background: DS.card,
};
const errorStyle = {
  color: DS.dangerText,
  fontSize: 11,
  marginTop: 3,
};
const btnPrimary = {
  background: DS.corail,
  color: "#fff",
  border: "none",
  borderRadius: DS.radiusControl,
  padding: "8px 16px",
  fontSize: 12,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
const btnSecondary = {
  background: DS.card,
  color: DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  padding: "8px 16px",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

// PR3 fix UX commit 10 — Styles bandeau mapping Beds24 read-only
const mappingBoxBase = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid",
  fontFamily: "inherit",
};
const mappingBoxOk = {
  ...mappingBoxBase,
  background: DS.successBg,
  borderColor: `${DS.success}55`,
  color: DS.successText,
};
const mappingBoxWarn = {
  ...mappingBoxBase,
  background: DS.warningBg,
  borderColor: DS.warning,
  color: DS.warningText,
};
const mappingBoxError = {
  ...mappingBoxBase,
  background: DS.dangerBg,
  borderColor: `${DS.danger}44`,
  color: DS.dangerText,
  fontSize: 12,
};
const mappingBoxNeutral = {
  ...mappingBoxBase,
  background: DS.pageBg,
  borderColor: DS.border,
  color: DS.textSecondary,
  fontSize: 12,
};
const codeStyle = {
  background: "rgba(255,255,255,0.6)",
  padding: "1px 6px",
  borderRadius: 4,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
};
